import { useCallback, useEffect, useRef } from 'react';

const BOTTOM_THRESHOLD_PX = 60;
const UPWARD_NOISE_PX = 2;

/**
 * Stick-to-bottom scroll pinning.
 *
 * Default mode: containerRef is the scrollable element itself.
 *
 * windowScroll mode: the PAGE is the scroller (document.scrollingElement) and
 * containerRef is only an anchor used to tell whether the chat is visible.
 * The chat lives in a tab: when another tab is active (the anchor is
 * display:none), scroll events belong to that tab, so they must not touch the
 * pin state - and we must never scroll the page out from under it (e.g. a
 * response still streaming in the hidden chat tab).
 */
export function useStickToBottom(containerRef, { windowScroll = false } = {}) {
  const pinnedRef = useRef(true);

  const getScroller = useCallback(
    () => (windowScroll ? document.scrollingElement : containerRef.current),
    [containerRef, windowScroll],
  );

  const isChatVisible = useCallback(
    () =>
      !windowScroll ||
      (containerRef.current && containerRef.current.offsetParent !== null),
    [containerRef, windowScroll],
  );

  useEffect(() => {
    const el = getScroller();
    if (!el) return undefined;
    const target = windowScroll ? window : el;

    let lastScrollTop = el.scrollTop;

    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const movedUp = el.scrollTop < lastScrollTop;
      // Track position even while the chat is hidden so the first scroll after
      // returning to the tab doesn't compare against a stale value
      lastScrollTop = el.scrollTop;
      if (!isChatVisible()) return;
      if (movedUp) {
        if (distFromBottom > UPWARD_NOISE_PX) {
          pinnedRef.current = false;
        }
      } else {
        pinnedRef.current = distFromBottom <= BOTTOM_THRESHOLD_PX;
      }
    };

    const onWheel = (event) => {
      if (!isChatVisible()) return;
      if (event.deltaY < 0) {
        pinnedRef.current = false;
      }
    };

    target.addEventListener('scroll', onScroll, { passive: true });
    target.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      target.removeEventListener('scroll', onScroll);
      target.removeEventListener('wheel', onWheel);
    };
  }, [getScroller, isChatVisible, windowScroll]);

  const scrollToBottom = useCallback(() => {
    const el = getScroller();
    if (el && isChatVisible()) {
      el.scrollTop = el.scrollHeight;
    }
  }, [getScroller, isChatVisible]);

  const scrollIfPinned = useCallback(() => {
    if (pinnedRef.current) {
      scrollToBottom();
    }
  }, [scrollToBottom]);

  const pin = useCallback(() => {
    pinnedRef.current = true;
    scrollToBottom();
  }, [scrollToBottom]);

  return { scrollIfPinned, pin };
}

export default useStickToBottom;
